"use client";
import "leaflet/dist/leaflet.css";
import "./osm.css";
import "../map.css";
import "./chart.css";

import { forwardRef, ReactElement, useImperativeHandle, useRef, useState } from "react";
import type { Map as LeafletMap } from "leaflet";
import { MapContainer, TileLayer } from "react-leaflet";

import { MapPositionLogger } from "./components/MapPositionLogger";
import { MapEventHandler } from "./components/MapEventHandler";
import { IMapOperation } from "./IMapOperation";
import { Coordinates } from "../types/MapData";
import { CURSOR, CursorDrawer } from "./components/CursorDrawer";

type Props = {
  centerChanged?: (val: Coordinates) => void;
  zoomChanged?: (val: number) => void;
  onReady?: () => void;
  cursorType?: CURSOR;

  onClick?: (position: Coordinates) => void;
  onDoubleClick?: (position: Coordinates) => void;

  mapFunctions?: ReactElement[];
};

export const OpenStreetMapViewer = forwardRef<IMapOperation, Props>((props: Props, ref) => {
  const { onReady, cursorType = CURSOR.CROSS_HAIR } = props;
  const mapRef = useRef<LeafletMap | null>(null);
  const onReadyCalledRef = useRef(false);
  const [markerPosition, setMarkerPosition] = useState<{ x: number; y: number } | null>(null);

  useImperativeHandle(ref, () => ({
    setPosition(coord: Coordinates, zoom?: number) {
      if (!mapRef.current) return;
      mapRef.current.setView([coord.y, coord.x], zoom);
    },
    setZoom(zoom: number) {
      if (!mapRef.current) return;
      mapRef.current.setZoom(zoom);
    },
  }));

  const handleMapRef = (map: LeafletMap | null) => {
    mapRef.current = map;
    if (map && onReady && !onReadyCalledRef.current) {
      onReadyCalledRef.current = true;
      onReady();
    }
  };

  const onMouseOver = (position: { x: number; y: number }) => {
    setMarkerPosition(position);
  };

  const onMapClick = (position: Coordinates) => {
    props.onClick?.(position);
  };

  const onMapDoubleClick = (position: Coordinates) => {
    props.onDoubleClick?.(position);
  };

  const onMouseLeave = () => {
    setMarkerPosition(null);
  };

  return (
    <div className="osm-map-wrapper" style={{ height: "100%", width: "100%" }}>
      <MapContainer
        ref={handleMapRef}
        className="osm-map"
        center={[35.681236, 139.767125]}
        zoom={13}
        scrollWheelZoom
        doubleClickZoom={false}
        style={{
          height: "100%",
          width: "100%",
        }}
      >
        <TileLayer
          attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        <MapPositionLogger centerChanged={props.centerChanged} zoomChanged={props.zoomChanged} />
        <MapEventHandler
          onMouseOver={onMouseOver}
          onMouseLeave={onMouseLeave}
          onMapClick={onMapClick}
          onMapDoubleClick={onMapDoubleClick}
        />
        {props.mapFunctions}
        {markerPosition && (
          <CursorDrawer cursorType={cursorType} left={markerPosition.x} top={markerPosition.y} />
        )}
      </MapContainer>
      {/* <div className="osm-center-crosshair" aria-hidden="true" style={{ zIndex: 9999 }} /> */}
    </div>
  );
});

OpenStreetMapViewer.displayName = "OpenStreetMap";
